import React, { ReactElement, useEffect, useState } from 'react';
import { CognitoUser } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { setAuthCookies, checkAuthCookies, checkFederatedRedirectUrl } from '../../utils/cookies-management';
import { editProfile, getProfile } from '../../utils/auth-functions';
import CardHeader from '../organisms/CardHeader';

export interface CognitoUserAttributes {
  name?: string;
  family_name?: string;
  nickname?: string;
  email?: string;
  ['custom:privacy_policy']?: string;
}

type ProfileUserSession = {
  accessToken: {
    jwtToken: string;
    payload?: {
      exp?: number;
    };
  };
  idToken: {
    jwtToken: string;
  };
  refreshToken: {
    token: string;
  };
};

type CustomCognitoUser = CognitoUser & { signInUserSession: ProfileUserSession; attributes: CognitoUserAttributes };

export default function Profile(): ReactElement {
  const [name, setName] = useState<string | undefined>();
  const [surname, setSurname] = useState<string | undefined>();
  const [nickname, setNickname] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [profile, setProfile] = useState<CustomCognitoUser | string | undefined>(undefined);
  const [error, setError] = useState<boolean>(false);
  const [privacyCheck, setPrivacyCheck] = useState<boolean | undefined>(undefined);
  const history = useHistory();
  const { search } = useLocation();
  const queryString = new URLSearchParams(search);
  const redirectUrl = queryString.get('redirectUrl');
  const inApp = queryString.get('inApp');

  const checkPrivacyPolicyAttribute = () => {
    return typeof profile === 'object' && !profile?.attributes['custom:privacy_policy'];
  };

  const handleEditProfile = () => {
    if (name || surname || nickname) {
      const tryEditProfile = async () => {
        const obj = checkPrivacyPolicyAttribute()
          ? { nickname: nickname || '', name: name || '', family_name: surname || '', 'custom:privacy_policy': privacyCheck ? '1' : '0' }
          : { nickname: nickname || '', name: name || '', family_name: surname || '' };

        editProfile(obj).then((res) => {
          if (res === 'SUCCESS') {
            if (redirectUrl) window.location.href = redirectUrl;
            else history.go(0);
          } else {
            setError(true);
          }
        });
      };
      tryEditProfile();
    }
  };

  const setTokens = (data: any) => {
    const {
      signInUserSession: { accessToken, idToken, refreshToken },
    } = data;
    if (typeof accessToken.jwtToken === 'string' && typeof idToken.jwtToken === 'string' && typeof refreshToken.token === 'string') {
      const tokens = {
        accessToken: {
          jwtToken: accessToken.jwtToken,
          payload: {
            exp: accessToken.payload.exp,
          },
        },
        idToken: {
          jwtToken: idToken.jwtToken,
        },
        refreshToken: refreshToken.token,
      };
      setAuthCookies(tokens);
    }
  };

  const handleRedirectPolicyPrivacy = () => {
    window.open('https://www.sergiobonelli.it/bonelli-digital-comics/2022/09/23/news/privacy-policy-1022152/', '_blank');
  };

  useEffect(() => {
    Hub.listen('auth', ({ payload: { event, data } }) => {
      if (event === 'signIn') {
        localStorage.setItem('tokens', JSON.stringify(data));
      }
    });
  }, []);

  useEffect(() => {
    const getProfileData = async () => {
      await getProfile().then((res) => setProfile(res));
    };
    getProfileData();
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('tokens') ? JSON.parse(localStorage.getItem('tokens') || '{}') : undefined;
    if (checkPrivacyPolicyAttribute()) {
      setPrivacyCheck(true);
    } else if (profile && !checkPrivacyPolicyAttribute() && storedToken) {
      setTokens(storedToken);
      localStorage.removeItem('tokens');
      const federatedRedirectUrl = checkFederatedRedirectUrl();
      if (federatedRedirectUrl) window.location.href = federatedRedirectUrl;
    }
  }, [profile]);

  if (profile && typeof profile !== 'string' && !checkAuthCookies()) {
    if (profile.signInUserSession) {
      const { signInUserSession } = profile;
      const { accessToken, idToken, refreshToken } = signInUserSession;
      const tokens = {
        accessToken: {
          jwtToken: accessToken.jwtToken,
          payload: {
            exp: accessToken?.payload?.exp,
          },
        },
        idToken: {
          jwtToken: idToken.jwtToken,
        },
        refreshToken: refreshToken.token,
      };
      setAuthCookies(tokens, true);
    }
  }

  useEffect(() => {
    if (profile && typeof profile !== 'string' && profile?.attributes) {
      const {
        attributes: { name: profileName, family_name: profileSurname, nickname: profileNickname, email: profileEmail },
      } = profile;
      setName(profileName);
      setSurname(profileSurname);
      setNickname(profileNickname);
      setEmail(profileEmail);
    }
    if (profile && profile === 'The user is not authenticated') {
      history.replace(inApp === 'true' ? '/?inApp=true' : '/');
    }
  }, [profile]);





  return (
    <div className="h-100">
      <div className="d-flex align-items-center h-100">
        <div className="mx-auto login-box">
          <div className="card">
            <CardHeader />
            <div className="card-body">
              {checkPrivacyPolicyAttribute()
                ? <h5 className="card-title text-center py-1">Registrazione</h5>
                : <h5 className="card-title text-center py-1">Profilo</h5>
              }
              {checkPrivacyPolicyAttribute()
                ? <p className="card-text">Ultimo paso per finire la registrazione completa i dati mancanti</p>
                : <p className="card-text">Imposta o aggiorna i tuoi dati o verifica i tuoi abbonamenti attivi</p>
              }
              <form id="edit-form">
                <div className="row">
                  <div className="col-sm-12">
                    <div className="form-group">
                      <input onChange={(e) => setName(e.target.value)} type="text" name="name" className="form-control" placeholder={name || 'Nome'} />
                    </div>
                  </div>
                  <div className="col-sm-12">
                    <div className="form-group">
                      <input onChange={(e) => setSurname(e.target.value)} type="text" name="family_name" className="form-control" placeholder={surname || 'Cognome'} />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <div className="form-group">
                      <input onChange={(e) => setNickname(e.target.value)} type="nickname" name="nickname" className="form-control" placeholder={nickname || 'Nickname'} />
                    </div>
                  </div>
                </div>
                {checkPrivacyPolicyAttribute()
                  && <div className="mt-3 mb-3">
                    <span role="button" onClick={() => handleRedirectPolicyPrivacy()}>
                      L’utente acconsente al trattamento dei propri dati personali per ricevere con modalità cartacee e/o automatizzate comunicazioni promozionali e commerciali e/o newsletter relative a prodotti/servizi offerti ed iniziative del titolare del trattamento.
                    </span>

                    <br />
                    <div className="mt-2">
                      <div className="d-inline-flex gap-2">
                        <input
                          className="form-check-input mr-2"
                          type="checkbox"
                          checked={privacyCheck}
                          onChange={() => setPrivacyCheck(!privacyCheck)}
                        />
                        <span>{'Acconsento'}</span>
                      </div>
                      <br />
                      <div className="d-inline-flex gap-2">
                        <input
                          className="form-check-input mr-2"
                          type="checkbox"
                          checked={!privacyCheck}
                          onChange={() => setPrivacyCheck(!privacyCheck)}
                        />
                        <span>{'Non acconsento'}</span>
                      </div>
                    </div>
                  </div>
                }
                <div id="consents"></div>
                {/* 
                  <div className="h-50 my-5">
                    <em><small>I campi contrassegnati da (*) sono obbligatori</small></em>
                  </div>
                */}
              </form>
              {email !== 'true' && (
                <div className="my-4 text-center">
                  <Link className="link-override" rel="keep-params" to={inApp === 'true' ? `/forgot-password?email=${email}&inApp=true` : `/forgot-password?email=${email}`}>
                    Premi qui
                  </Link>
                  &nbsp;se desideri aggiornare la tua password.
                </div>
              )}
              <div className="my-4 text-center">
                <Link className="link-alert" rel="keep-params" to={inApp === 'true' ? '/delete-profile?inApp=true' : '/delete-profile'}>
                  Elimina il tuo account.
                </Link>
              </div>
              <div className="form-group profile mt-5 d-flex align-items-center justify-content-around">
                <button className="btn btn-primary mt-2 mb-5" disabled={!name && !surname && !nickname} onClick={() => handleEditProfile()}>
                  Salva
                </button>
                <Link className="btn btn-primary mt-2 mb-5" to={'/subscriptions'}>
                  <button id="lista-abbonamenti-button" className="btn btn-secondary pull-right">
                    Abbonamenti{' '}
                  </button>
                </Link>
              </div>
              {error && <div className="input-not-valid text-center m-0">Si è verificato un errorre, ti invitiamo a riprovare più tardi</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
